import {useEffect, useMemo, useState} from 'react'
import {getBreakpoint, getViewPort} from '../../_metronic/assets/ts/_utils/DomHelpers'

type BreakpointKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

interface ResponsiveOptions {
  // Optional: override default/fetched breakpoints
  breakpoints?: Partial<Record<BreakpointKey, number>>
}

export interface ResponsiveInfo {
  width: number
  height: number
  breakpoint: BreakpointKey
  isMobile: boolean
  isTablet: boolean
  isIpad: boolean
  isDesktop: boolean
  device: 'mobile' | 'tablet' | 'ipad' | 'desktop'
}

const getBootstrapBreakpoints = (): Record<BreakpointKey, number> => {
  // Try to read from CSS variables (preferred)
  const sm = Number(getBreakpoint('sm')) || 576
  const md = Number(getBreakpoint('md')) || 768
  const lg = Number(getBreakpoint('lg')) || 992
  const xl = Number(getBreakpoint('xl')) || 1200
  const xxl = Number(getBreakpoint('xxl')) || 1400
  // xs is implied as 0
  return {xs: 0, sm, md, lg, xl, xxl}
}

const detectIpad = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const platform = (navigator)?.platform || ''
  const maxTouchPoints = (navigator)?.maxTouchPoints || 0

  // Classic iPad
  if (/iPad/i.test(ua)) return true

  // iPadOS 13+ identifies as Mac, detect via touch capability
  if (platform === 'MacIntel' && maxTouchPoints > 1) return true

  return false
}

export function useResponsive(options?: ResponsiveOptions): ResponsiveInfo {
  const customBreakpoints = options?.breakpoints

  const getSize = () => {
    if (typeof window === 'undefined') {
      return {width: 0, height: 0}
    }
    const {width, height} = getViewPort()
    return {width, height}
  }

  const [size, setSize] = useState(getSize)

  useEffect(() => {
    const handleResize = () => {
      setSize(getSize())
    }
    window.addEventListener('resize', handleResize, {passive: true})
    return () => window.removeEventListener('resize', handleResize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const info = useMemo<ResponsiveInfo>(() => {
    const bp = {...getBootstrapBreakpoints(), ...(customBreakpoints || {})}
    const w = size.width
    const h = size.height

    let current: BreakpointKey = 'xs'
    if (w >= bp.xxl) current = 'xxl'
    else if (w >= bp.xl) current = 'xl'
    else if (w >= bp.lg) current = 'lg'
    else if (w >= bp.md) current = 'md'
    else if (w >= bp.sm) current = 'sm'
    else current = 'xs'

    const isIpad = detectIpad()
    const isMobile = !isIpad && w < bp.md
    const isTablet = !isIpad && w >= bp.md && w < bp.lg
    const isDesktop = !isIpad && w >= bp.lg

    let device: ResponsiveInfo['device'] = 'desktop'
    if (isIpad) device = 'ipad'
    else if (isMobile) device = 'mobile'
    else if (isTablet) device = 'tablet'
    else device = 'desktop'

    return {
      width: w,
      height: h,
      breakpoint: current,
      isMobile,
      isTablet,
      isIpad,
      isDesktop,
      device,
    }
  }, [size.width, size.height, customBreakpoints])

  return info
}

export default useResponsive


